import { useEffect, useState } from "react";
import { AlertTriangle, Check, Send, ShieldAlert } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "../../components/ui/sheet";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import { Checkbox } from "../../components/ui/checkbox";
import { Avatar } from "../../components/kit";
import { api, errMsg, label, fmtDate } from "../../lib/api";
import { toast } from "sonner";

export const BottomSheet = ({ open, onClose, title, desc, testid, children }) => (
  <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
    <SheetContent side="bottom" data-testid={testid} className="mx-auto max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-t-[22px] border-[#E6E9ED] px-4 pb-8 pt-3 sm:px-6">
      <div className="mx-auto mb-3 h-1.5 w-12 rounded-full bg-[#C5CBD1]" />
      <SheetHeader className="text-left"><SheetTitle className="text-lg font-extrabold">{title}</SheetTitle><SheetDescription className={desc ? "" : "sr-only"}>{desc || title}</SheetDescription></SheetHeader>
      <div className="mt-4">{children}</div>
    </SheetContent>
  </Sheet>
);

const OFFENCES = {
  yellow: ["Unsporting behaviour", "Dissent", "Persistent offences", "Delaying the restart", "Failure to respect distance", "Entering/leaving without permission"],
  red: ["Serious foul play", "Violent conduct", "Spitting", "DOGSO — handball", "DOGSO — foul", "Offensive/insulting language"],
  foul: ["Tripping", "Pushing", "Holding", "Handball", "Dangerous play", "Impeding", "Charging", "Other"],
};
const ACTS = [
  ["yellow", "Yellow Card", "bg-[#FFD400] text-[#171B20]"], ["red", "Red Card", "bg-[#F02030] text-white"],
  ["goal", "Goal", "bg-[#171B20] text-white"], ["own_goal", "Own Goal", "bg-[#E7EAEE] text-[#171B20]"],
  ["foul", "Foul", "bg-[#343A40] text-white"], ["penalty", "Penalty", "bg-[#E7EAEE] text-[#171B20]"],
  ["substitution", "Substitution", "bg-[#E7EAEE] text-[#171B20]"], ["note", "Incident Note", "bg-white border border-[#C5CBD2] text-[#171B20]"],
];
const PEN = ["penalty_awarded", "penalty_scored", "penalty_missed"];
const BIG = "min-h-[56px] rounded-[14px] text-sm font-extrabold uppercase tracking-wide transition-transform active:scale-[0.97]";

const Chips = ({ items, value, onChange, testid }) => (
  <div className="flex flex-wrap gap-1.5" data-testid={testid}>
    {items.map((o) => <button key={o} type="button" data-testid={`${testid}-${o.toLowerCase().replace(/[^a-z]+/g, "-")}`} onClick={() => onChange(o === value ? "" : o)}
      className={`min-h-[40px] rounded-xl px-3 text-xs font-bold ${value === o ? "bg-[#171B20] text-white" : "bg-[#F7F8FA] text-[#343A40]"}`}>{label(o)}</button>)}
  </div>
);

export const PlayerStatus = ({ st }) => (
  <div className="mt-1 flex flex-wrap gap-1.5" data-testid="player-discipline-status">
    {st?.red ? <span className="rounded-md bg-[#F02030] px-2 py-0.5 text-[11px] font-bold text-white">Sent off</span> :
      st?.yellow ? <span className="rounded-md bg-[#FFD400] px-2 py-0.5 text-[11px] font-bold">Cautioned ×{st.yellow}</span> :
        <span className="rounded-md bg-[#E7EAEE] px-2 py-0.5 text-[11px] font-bold text-[#343A40]">No cautions</span>}
    {st?.goals > 0 && <span className="rounded-md bg-[#171B20] px-2 py-0.5 text-[11px] font-bold text-white">Goals {st.goals}</span>}
  </div>
);

export function PlayerSheet({ sel, match, onClose, onRecord }) {
  const [step, setStep] = useState(null);
  const [offence, setOffence] = useState("");
  const [note, setNote] = useState("");
  const [pen, setPen] = useState("penalty_awarded");
  const [subIn, setSubIn] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setStep(sel?.initial || null); setOffence(""); setNote(""); setSubIn(null); }, [sel]);
  if (!sel) return null;
  const { player: p, team } = sel;
  const st = match.player_state?.[p.id] || {};
  const second = !!st.yellow && !st.red;
  const allowed = st.red ? ["note"] : p.bench ? ["yellow", "red", "note"] : ACTS.map((a) => a[0]);
  const go = async () => {
    setBusy(true);
    try {
      const notes = [offence && `Offence: ${offence}`, note].filter(Boolean).join(" — ") || undefined;
      await onRecord(step === "penalty" ? pen : step, team, p, { notes, ...(step === "substitution" ? { player_in_id: subIn } : {}) });
      onClose();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const band = step === "yellow" ? (second ? "bg-[#F02030] text-white" : "bg-[#FFD400] text-[#171B20]") : step === "red" ? "bg-[#F02030] text-white" : "bg-[#171B20] text-white";
  const ok = step === "note" ? note.trim() : step === "substitution" ? subIn : true;
  return (
    <BottomSheet open onClose={onClose} title="Player actions" testid="player-action-menu">
      <div className="flex items-center gap-4 rounded-[18px] tro-metal p-3" data-testid="action-player-info">
        <Avatar player={p} size={72} />
        <div className="min-w-0">
          <div className="truncate text-xl font-extrabold">#{p.number} {p.first_name} {p.last_name}</div>
          <div className="text-sm font-semibold text-[#343A40]">{team.name} · {p.position}{p.bench ? " · Substitute" : ""}</div>
          <PlayerStatus st={st} />
        </div>
      </div>
      {!step && <div className="mt-4 grid grid-cols-2 gap-2">
        {ACTS.filter((a) => allowed.includes(a[0])).map(([k, l, c]) => (
          <button key={k} data-testid={`action-${k}`} onClick={() => setStep(k)} className={`${BIG} ${k === "yellow" && second ? "bg-gradient-to-br from-[#FFD400] from-50% to-[#F02030] to-50% text-[#171B20]" : c}`}>
            {k === "yellow" && second ? "Second Caution" : l}
          </button>
        ))}
      </div>}
      {step && <div className="mt-4 space-y-3" data-testid={`confirm-sheet-${step}`}>
        <div className={`rounded-[14px] p-4 text-center text-lg font-extrabold uppercase ${band}`} data-testid="confirm-banner">
          {step === "yellow" && second ? "Second caution · Dismissal" : `Confirm ${label(step)}`}
        </div>
        {step === "yellow" && second && <div className="flex gap-2 rounded-[14px] border-2 border-[#F02030] bg-[#F02030]/5 p-3 text-sm font-semibold" data-testid="second-caution-warning">
          <ShieldAlert className="h-5 w-5 shrink-0 text-[#F02030]" />#{p.number} {p.short_name} has already been cautioned. Confirming records the second caution and dismisses the player.
        </div>}
        {OFFENCES[step] && <div><div className="tro-label mb-1.5">{step === "foul" ? "Foul type" : "Offence"}</div><Chips items={OFFENCES[step]} value={offence} onChange={setOffence} testid="offence" /></div>}
        {step === "penalty" && <div><div className="tro-label mb-1.5">Penalty outcome</div><Chips items={PEN} value={pen} onChange={(v) => setPen(v || "penalty_awarded")} testid="pen" />
          <p className="mt-1.5 text-xs text-[#6C757D]">A penalty award does not change the score. Only "Penalty scored" adds a goal.</p></div>}
        {step === "substitution" && <div><div className="tro-label mb-1.5">Incoming substitute</div>
          {!sel.bench?.length ? <div className="text-sm text-[#6C757D]">No substitutes available.</div> :
            <div className="grid max-h-56 grid-cols-2 gap-2 overflow-auto">{sel.bench.map((b) => (
              <button key={b.id} data-testid={`sub-in-${b.number}`} onClick={() => setSubIn(b.id)} className={`flex items-center gap-2 rounded-[14px] border p-2 text-left ${subIn === b.id ? "border-[#171B20] bg-[#F7F8FA]" : "border-[#E7EAEE]"}`}>
                <Avatar player={b} size={36} /><span className="text-sm font-bold">#{b.number} {b.short_name}</span>
              </button>))}</div>}</div>}
        <Textarea data-testid="action-note-input" className="rounded-xl" placeholder={step === "note" ? "Incident note (required)" : "Optional note"} value={note} onChange={(e) => setNote(e.target.value)} />
        <div className="grid grid-cols-2 gap-2">
          <Button variant="outline" className={BIG} data-testid="confirm-back-btn" onClick={() => (sel.initial ? onClose() : setStep(null))}>Back</Button>
          <Button data-testid="confirm-action-btn" disabled={busy || !ok} className={`${BIG} ${step === "yellow" && !second ? "bg-[#FFD400] text-[#171B20] hover:bg-[#f0c800]" : ["red", "yellow"].includes(step) ? "bg-[#F02030] hover:bg-[#d81a29]" : ""}`} onClick={go}>
            <Check className="h-4 w-4" />Confirm
          </Button>
        </div>
      </div>}
    </BottomSheet>
  );
}

const ELIG = (p) => (p?.registration_status && !["approved", "registered"].includes(p.registration_status) ? label(p.registration_status) : p?.status === "suspended" ? "Suspended" : "Eligible");

export function RefSubRequests({ m, ping, viewOnly }) {
  const [list, setList] = useState([]);
  const load = () => api.get(`/matches/${m.id}/substitution-requests`).then((r) => setList(r.data.filter((x) => ["pending", "verified"].includes(x.status)))).catch(() => {});
  useEffect(() => { load(); }, [m.id, ping, m.status]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!list.length) return null;
  const P = (id) => m.players[id];
  const decide = async (r, approve) => {
    try { await api.post(`/substitution-requests/${r.id}/process`, approve ? { approve: true } : { approve: false, reason: "Rejected by the referee" }); toast.success(approve ? "Substitution validated — official event recorded" : "Substitution rejected"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <section className="tro-card border-2 border-[#171B20]/15 p-3" data-testid="ref-sub-requests">
      <div className="tro-label mb-2">Substitutions to validate · {list.length}</div>
      <div className="space-y-2">{list.map((r) => (
        <div key={r.id} className="rounded-[14px] bg-[#F7F8FA] p-2.5" data-testid={`ref-subreq-${r.id}`}>
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span className="rounded bg-[#171B20] px-1.5 text-[10px] font-extrabold text-white">{r.team_id === m.home.team_id ? m.home.short : m.away.short}</span>
            <b className="text-[#F02030]">OUT #{P(r.player_out_id)?.number} {P(r.player_out_id)?.short_name}</b>
            <b className="text-[#35865C]">IN #{P(r.player_in_id)?.number} {P(r.player_in_id)?.short_name}</b>
            <span className={`ml-auto rounded-full px-2 py-0.5 text-[10px] font-extrabold uppercase ${r.status === "verified" ? "bg-[#35865C] text-white" : "bg-[#E7EAEE]"}`}>{r.status === "verified" ? "Verified by 4th official" : "Coach request"}</span>
          </div>
          <div className="mt-2 grid grid-cols-2 gap-2">
            <Button className="h-11 rounded-[12px]" disabled={viewOnly} data-testid={`ref-validate-sub-${r.id}`} onClick={() => decide(r, true)}><Check className="h-4 w-4" />Validate</Button>
            <Button variant="outline" className="h-11 rounded-[12px]" disabled={viewOnly} data-testid={`ref-reject-sub-${r.id}`} onClick={() => decide(r, false)}>Reject</Button>
          </div>
        </div>
      ))}</div>
    </section>
  );
}

export function TeamSheets({ open, onClose, match: m, reload, canApprove = true }) {
  const approve = async (tid) => { try { await api.post(`/matches/${m.id}/lineup/${tid}/approve`); toast.success("Team sheet approved"); reload(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <BottomSheet open={open} onClose={onClose} title="Team sheets" desc="Review only. Registration records are managed by clubs." testid="team-sheets-sheet">
      <div className="grid gap-4 md:grid-cols-2">
        {[m.home, m.away].map((s) => {
          const lu = m.lineups.find((l) => l.team_id === s.team_id);
          const row = (pid, sub) => { const p = m.players[pid]; return p && (
            <li key={pid} className="flex items-center gap-2 rounded-xl border border-[#E7EAEE] px-2 py-1.5 text-sm" data-testid={`sheet-player-${s.short}-${p.number}`}>
              <Avatar player={p} size={32} /><b className="w-7 font-mono">{p.number}</b><span className="min-w-0 flex-1 truncate font-semibold">{p.short_name}</span>
              <span className="text-[10px] font-bold uppercase text-[#6C757D]">{sub ? "SUB" : p.position}</span>
              <span className={`rounded-md px-1.5 py-0.5 text-[10px] font-bold ${ELIG(p) === "Eligible" ? "bg-[#35865C]/10 text-[#35865C]" : "bg-[#F02030]/10 text-[#F02030]"}`}>{ELIG(p)}</span>
            </li>); };
          return (
            <div key={s.team_id} data-testid={`team-sheet-${s.short}`}>
              <div className="mb-2 flex items-center justify-between"><b>{s.name}</b><span className="text-xs font-bold text-[#6C757D]">{lu ? `${lu.formation} · ${label(lu.status)}` : "Not submitted"}</span></div>
              {lu && <><div className="tro-label mb-1">Starting XI</div><ul className="space-y-1">{lu.starting.map((x) => row(x))}</ul>
                <div className="tro-label mb-1 mt-3">Substitutes</div><ul className="space-y-1">{lu.subs.map((x) => row(x, true))}</ul></>}
              {canApprove && lu?.status === "submitted" && <Button className="mt-3 w-full rounded-xl" data-testid={`approve-lineup-${s.short}`} onClick={() => approve(s.team_id)}>Approve team sheet</Button>}
            </div>
          );
        })}
      </div>
    </BottomSheet>
  );
}

export const CHECKLIST = ["Team sheets reviewed", "Player eligibility verified", "Field inspected", "Equipment inspected", "Officials confirmed", "Device tested", "Match readiness confirmed"];

export function PrepSheet({ open, onClose, match: m, reload }) {
  const saved = m.checklists?.referee?.items || {};
  const [items, setItems] = useState(() => Object.fromEntries(CHECKLIST.map((k) => [k, !!saved[k]])));
  const confirmed = (m.my_official_roles || []).every((r) => m.confirmations?.[r]);
  const done = Object.values(items).filter(Boolean).length + (confirmed ? 1 : 0);
  const act = async (fn, msg) => { try { await fn(); toast.success(msg); reload(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <BottomSheet open={open} onClose={onClose} title="Pre-match checklist" desc={`${m.match_number} · ${m.venue} · ${fmtDate(m.kickoff)}`} testid="prematch-panel">
      <div className="mb-3 flex items-center gap-3"><div className="h-2 flex-1 overflow-hidden rounded-full bg-[#E7EAEE]"><div className="h-full bg-[#35865C] transition-[width]" style={{ width: `${(done / 8) * 100}%` }} /></div><b className="text-sm" data-testid="prep-progress">{done}/8</b></div>
      <div className="space-y-2">
        <div className="flex min-h-[52px] items-center justify-between rounded-[14px] border border-[#E7EAEE] px-3">
          <span className="flex items-center gap-2 text-sm font-semibold">{confirmed ? <Check className="h-4 w-4 text-[#35865C]" /> : <AlertTriangle className="h-4 w-4 text-[#C5CBD2]" />}Accept appointment</span>
          {confirmed ? <span className="text-xs font-bold text-[#35865C]" data-testid="appointment-confirmed">Accepted</span> :
            <Button size="sm" data-testid="confirm-appointment-btn" onClick={() => act(() => api.post(`/matches/${m.id}/confirm`), "Appointment confirmed")}>Accept</Button>}
        </div>
        {CHECKLIST.map((k) => (
          <label key={k} className="flex min-h-[52px] cursor-pointer items-center gap-3 rounded-[14px] border border-[#E7EAEE] px-3 text-sm font-semibold">
            <Checkbox data-testid={`check-${k.split(" ")[0].toLowerCase()}`} checked={items[k]} onCheckedChange={(v) => setItems({ ...items, [k]: !!v })} />{k}
          </label>
        ))}
      </div>
      <Button className="mt-4 h-12 w-full rounded-xl font-extrabold" data-testid="save-checklist-btn" onClick={() => act(() => api.put(`/matches/${m.id}/checklists/referee`, { items }), "Checklist saved")}>Save checklist</Button>
    </BottomSheet>
  );
}

export const SIGNALS = ["Offside observation", "Foul observation", "Substitution ready", "Technical area alert", "Equipment issue", "Request review"];

export const CommsSheet = ({ open, onClose, ...rest }) => (
  <BottomSheet open={open} onClose={onClose} title="Officials communication" desc="Private to appointed officials. Observations are advisory and never become decisions automatically." testid="comms-sheet">
    {open && <CommsFeed {...rest} />}
  </BottomSheet>
);

export function CommsFeed({ id, me, ping, events = [], signals = SIGNALS }) {
  const [list, setList] = useState([]);
  const [txt, setTxt] = useState("");
  const [ref, setRef] = useState("");
  const load = () => api.get(`/matches/${id}/communications`).then((r) => setList(r.data)).catch(() => {});
  useEffect(() => { load(); }, [id, ping]); // eslint-disable-line react-hooks/exhaustive-deps
  const send = async (text, kind = "message") => {
    try { await api.post(`/matches/${id}/communications`, { text, kind, event_id: ref || undefined }); setTxt(""); setRef(""); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const ack = async (c) => { try { await api.post(`/matches/${id}/communications/${c.id}/ack`); load(); } catch (e) { toast.error(errMsg(e)); } };
  const recent = [...events].filter((e) => e.status === "active" && e.player_id).slice(-6).reverse();
  return (
    <div data-testid="comms-feed">
      <div className="tro-label mb-1.5">Quick signals</div>
      <div className="mb-3 grid grid-cols-2 gap-2 sm:grid-cols-3">{signals.map((s) => (
        <button key={s} data-testid={`signal-${s.split(" ")[0].toLowerCase()}`} onClick={() => send(s, "signal")} className="min-h-[48px] rounded-[14px] border border-[#E7EAEE] bg-[#F7F8FA] px-2 text-xs font-bold hover:bg-[#171B20] hover:text-white">{s}</button>
      ))}</div>
      <div className="max-h-[38vh] space-y-2 overflow-auto rounded-[14px] bg-[#F7F8FA] p-2" data-testid="comms-list">
        {!list.length && <div className="p-3 text-center text-sm text-[#6C757D]">No messages yet.</div>}
        {list.map((c) => {
          const acked = (c.acks || []).some((a) => a.user_id === me.user.id);
          return (
            <div key={c.id} className={`rounded-xl bg-white p-2.5 text-sm ${c.kind !== "message" ? "border-l-4 border-[#FFD400]" : ""}`} data-testid={`comm-${c.id}`}>
              <div className="flex items-center gap-2"><b>{c.from_name}</b><span className="text-[10px] font-bold uppercase text-[#6C757D]">{label(c.role)}</span>{c.kind !== "message" && <span className="rounded bg-[#FFD400] px-1.5 text-[10px] font-bold uppercase">{label(c.kind)}</span>}</div>
              <div className="mt-0.5">{c.text}</div>
              {c.event_ref && <div className="mt-0.5 text-xs text-[#6C757D]">Re: {c.event_ref}</div>}
              <div className="mt-1 flex items-center justify-between text-[11px] text-[#6C757D]">
                <span data-testid={`comm-acks-${c.id}`}>{c.acks?.length ? `Acknowledged by ${c.acks.map((a) => a.name.split(" ")[0]).join(", ")}` : "Not acknowledged"}</span>
                {c.from_id !== me.user.id && !acked && <button data-testid={`ack-${c.id}`} onClick={() => ack(c)} className="rounded-lg border border-[#C5CBD2] px-2 py-1 font-bold text-[#171B20]">Acknowledge</button>}
              </div>
            </div>
          );
        })}
      </div>
      {recent.length > 0 && <select data-testid="comms-event-ref" value={ref} onChange={(e) => setRef(e.target.value)} className="mt-3 h-11 w-full rounded-xl border border-[#E7EAEE] px-3 text-sm">
        <option value="">No incident reference</option>{recent.map((e) => <option key={e.id} value={e.id}>{e.minute + 1}' {label(e.type)}</option>)}
      </select>}
      <div className="mt-2 flex gap-2"><input data-testid="comms-input" value={txt} onChange={(e) => setTxt(e.target.value)} className="h-12 flex-1 rounded-xl border border-[#E7EAEE] px-3 text-sm" placeholder="Message to match officials" />
        <Button data-testid="comms-send-btn" disabled={!txt.trim()} className="h-12 rounded-xl" onClick={() => send(txt)}><Send className="h-4 w-4" />Send</Button></div>
    </div>
  );
}
